/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Inject, OnInit } from '@angular/core';
import { ITextButton } from 'libs/sysque/shared/ui/src/lib/components/sysque-text-button/sysque-text-button-types';
import { IFamilyInformationItem, Param } from '../add-family-information-types';
import { Observable } from 'rxjs';
import {
  IProjectDataFacade,
  PROJECT_DATA_FACADE_TOKEN,
} from '../../../../../../data-access/src/lib/domain/facades-models/project-data-facade-interface';
import { GlobalConstants } from '../../../constants/global-constants';
import { AlertStatus, IAlert } from '../../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-project-data',
  templateUrl: './project-data-component.html',
  styleUrls: ['./project-data-component.scss'],
  standalone: false
})

/**
 * Represents a component for managing project data.
 * This component is responsible for displaying and managing project parameters and their values.
 */
export class ProjectDataComponent implements OnInit {
  public componentTitle = '';
  public showCloseButton = true;
  public warningMessage = '';
  public alertStatus = AlertStatus.Warning;
  public revitDocParameterList: Param[] = [];
  public projectParameterList: Param[] = [];
  public params$: Observable<Param[]>;
  public currentCategory: string;
  public isParameterDropDownOpen = false;
  public isPushCheckBoxVisible = false;
  public editFamilyItem: IFamilyInformationItem;
  public selectedParameter: string;
  public projectParameters: Param[] = [];
  public projectParamNeedToModify: Param;
  public pushParameters: string;
 
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  constructor(@Inject(PROJECT_DATA_FACADE_TOKEN) private facade: IProjectDataFacade) {}

  //#region Public methods

  /**
   * Initializes the component.
   * This method is called after the component has been created and initialized.
   * It is a lifecycle hook that is called once, after the first `ngOnChanges`.
   * Use this method to perform any initialization logic for the component.
   */
  public ngOnInit(): void {
    this.componentTitle = GlobalConstants.PROJECT_DATA_DIALOG;
    window.addEventListener('storage', this.storageEventListener.bind(this));

    this.editFamilyItem = this.facade.getEditFamilyItem();
    this.pushParameters = this.editFamilyItem.pushParameters ?? '';

    this.resetData();
    this.updateLocalParams();
    this.fillParams();
    this.isPushValueChecked();
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * It removes the event listener for the 'storage' event.
   */
  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageEventListener.bind(this));
    this.facade.updateLocalStorage(GlobalConstants.ADD_PROJECT_PARAMETER_RESPONSE, {
      response: -1,
      paramList: undefined,
    });
  }

  /**
   * Handles the storage event listener.
   *
   * @param event - The storage event object.
   */
  public storageEventListener(event: StorageEvent): void {
    if (event && event.key === GlobalConstants.UPDATE_PROJECT_PARAMS_SIZES) {
      const data = JSON.parse(event.newValue);
      if (data.isSaved === 'true') {
        this.resetData();
        this.projectParameters = this.facade.getProjectParametersNeedToModify(false) as Param[];
        this.projectParameters.find((x) => x.index === this.projectParamNeedToModify.index).lstSizes = data.paramSizes;

        this.fillParams();
        this.facade.updateLocalStorage(GlobalConstants.UPDATE_PROJECT_PARAMS_SIZES, {
          isSaved: 'false',
          paramSizes: [],
        });
      }
    }
  }

  /**
   * Fills the parameters of the item.
   * If the item's parameters are an array, it iterates through each parameter.
   * If the parameter's name matches the selected parameter, it sets the value to 'Multiple values'
   * and adds it to the projectParameterList.
   * Otherwise, it adds the parameter to the projectParameterList.
   * Finally, it calls the onParameterDropDownOpened method.
   */
  public fillParams(): void {
    this.projectParameters.forEach((element: Param) => {
      this.projectParameterList.push(element);
    });

    this.fetchRevitParameters();
  }

  /**
   * Retrieves the selected parameter for a project.
   * @param projectParameter - The parameter to search for.
   * @returns The index of the parameter in the `revitDocParameterList` as a string, or `undefined` if not found.
   */
  public getSelectedParameter(projectParameter: Param): string {
    const result = this.revitDocParameterList.findIndex((x) => x.name == projectParameter.name);
    if (result !== -1) return result.toString();
  }

  /**
   * Handles the event when the parameter dropdown is opened.
   * Retrieves the category based on the item type and fetches the parameters from the document.
   */
  public onParameterDropDownOpened(): void {
    const result = this.facade.getAddProjectParameterResponse();

    if (result != undefined && result['paramList'] != undefined && result['response'] != -1) {
      this.revitDocParameterList = [];
      result['paramList'].forEach((element) => {
        const newProjectParam = {
          name: element.Name,
          value: element.Value,
          type: element.Type,
          category: element.Category,
          group: element.Group,
          groupParameterUnder: element.GroupParameterUnder,
          isInstance: element.IsInstance,
          isShared: element.IsShared,
          lstSizes: [],
          isPushOption: element.IsPushOption,
        } as Param;
        this.revitDocParameterList.push(newProjectParam);
      });
    }

    this.isParameterDropDownOpen = false;
  }

  /**
   * Fetches the Revit parameters for the current category and updates the local storage.
   */
  public fetchRevitParameters(): void {
    this.currentCategory = this.facade.getFamilyInformationResponse().familyCategoryName;
    this.facade.updateLocalStorage(GlobalConstants.PROJECT_DATA_CURRENT_CATEGORY, this.currentCategory);
    this.params$ = this.facade.readParametersFromDocument(this.currentCategory);

    this.params$.subscribe((data) => {
      this.revitDocParameterList = data;
      this.facade.updateLocalStorage(GlobalConstants.ADD_PROJECT_PARAMETER_RESPONSE, {
        response: -1,
        paramList: this.revitDocParameterList,
      });
    });
  }

  /**
   * Gets the configuration for the "Add Row" button.
   * @returns The configuration object for the "Add Row" button.
   */
  public get addRowButtonValues(): ITextButton<void> {
    return {
      name: 'Add Row',
      width: '100%',
      isBordered: true,
      imageSrc: 'assets/img/slide_add.png',
      click: () => {
        this.onAddRowClick();
      },
    };
  }

  /**
   * Adds a new row to the project parameter list.
   */
  public onAddRowClick(): void {
    const newProjectParam = {
      name: '',
      value: '',
      type: '',
      category: '',
      group: '',
      groupParameterUnder: '',
      isInstance: '',
      isShared: '',
      lstSizes: [],
      isPushOption: false,
      index: this.projectParameterList.length,
    } as Param;

    this.projectParameterList.push(newProjectParam);
  }

  /**
   * Checks if push value is enabled for the given project parameter.
   * @param projectParameter - The project parameter to check.
   * @returns A boolean indicating whether push value is enabled.
   */
  public isPushValueEnabled(projectParameter: Param): boolean {
    if (projectParameter.category) {
      if (
        projectParameter.category.split(',').length === 3 &&
        ((projectParameter.category.includes('Conduits') &&
          projectParameter.category.includes('Conduit Fittings') &&
          projectParameter.category.includes('Conduit Runs')) ||
          (projectParameter.category.includes('Cable Trays') &&
            projectParameter.category.includes('Cable Tray Fittings') &&
            projectParameter.category.includes('Cable Tray Runs')))
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Handles the event when a project parameter value is changed.
   * @param event - The event object containing the changed value.
   * @param projectParameter - The project parameter object.
   * @param rowIndex - The index of the row where the parameter value is changed.
   */
  public onProjectParameterValueChanged(event: any, projectParameter: Param, rowIndex: number): void {
    if (event.event != undefined) {
      this.pushUpdatedProjectParameterValue(projectParameter, rowIndex, event);
      this.saveLocalProjectParametersData();
      this.isParameterDropDownOpen = false;
    }
  }

  /**
   * Checks if the given project parameter is included in the push parameters of the item.
   * @param projectParameter - The project parameter to check.
   * @returns True if the project parameter is included in the push parameters, false otherwise.
   */
  public isPushValueChecked(): void {
    this.projectParameterList.forEach((param) => {
      const result = this.pushParameters?.split(',').filter((x) => x == param.name);
      if (this.pushParameters != '' && result != undefined && result[0] != '' && result.length > 0) {
        return (param.isPushedChecked = true);
      } else {
        return (param.isPushedChecked = false);
      }
    });
  }

  /**
   * Handles the change event when the value of a push parameter checkbox is modified.
   * @param event - The change event object.
   * @param projectParameter - The project parameter object.
   */
  public onPushValueChanged(event: any, projectParameter: Param): void {
    const itemIndex = this.projectParameterList.findIndex((x) => x.index === projectParameter.index);
    if (event.detail) {
      this.updatePushParametersIfPushIsTrue(itemIndex);
    } else {
      this.updatePushParametersIfPushIsFalse(itemIndex);
    }
    this.updatePushParameterInEditFamilyItem(projectParameter, false);
  }

  /**
   * Deletes a project parameter from the project parameter list.
   * @param projectParameter - The project parameter to be deleted.
   */
  public deleteProjectParameter(projectParameter: Param): void {
    this.updatePushParameterInEditFamilyItem(projectParameter);
    this.projectParameterList = this.projectParameterList.filter((x) => x !== projectParameter);
    this.projectParameterList = this.projectParameterList.filter((x) => x.index !== projectParameter.index);
  }

  /**
   * Modifies the project parameter sizes data.
   *
   * @param projectParameter - The parameter data to be modified.
   */
  public modifyProjectParameterSizesData(projectParameter: Param): void {
    // Create a new popup dialog configuration
    if (projectParameter.name === '') {
      this.facade.changeDialogDimensions(680);
      this.warningMessage = GlobalConstants.INPUT_PARAMETER_NAME_ERROR;
    } else {
      this.selectedParameter = projectParameter.name;
      this.projectParamNeedToModify = projectParameter;
      this.facade.updateLocalStorage(GlobalConstants.PROJECT_PARAM_TO_MODIFY, {
        needToEdit: projectParameter,
        paramList: this.projectParameterList,
      });
      this.facade.sendPopupRoutingAction(GlobalConstants.ASSIGN_PROJECT_DIALOG);
    }
  }

  /**
   * Determines whether to set a class for multiple value text.
   * @param event - The event parameter.
   * @returns A boolean indicating whether to set the class.
   */
  public setClassForMultipleValueText(event: Param): boolean {
    if (event.lstSizes != undefined && event.lstSizes.length > 0) {
      return true;
    }
    return false;
  }

  /**
   * Retrieves the text value of a parameter.
   * If the parameter has multiple sizes, it returns a constant value.
   * Otherwise, it returns the value of the parameter.
   * @param event - The parameter object.
   * @returns The text value of the parameter.
   */
  public getParameterTextValue(event: Param): string {
    if (event.lstSizes != undefined && event.lstSizes.length > 0) {
      // Return a constant value for parameters with multiple sizes
      return GlobalConstants.PARAMETER_TEXT_MULTIPLE_VALUES;
    }
    return event.value;
  }

  /**
   * Saves the data by validating it, saving project parameters data, and closing the window.
   */
  public saveData(): void {
    if (!this.validateData()) {
      return;
    }
    this.updateData();
    this.facade.closeWindow();
  }

  /**
   * Validates the project data.
   * Checks if the project parameter list has any empty names, duplicate parameters, or empty values.
   * Sets the warning message accordingly.
   */
  public validateData(): boolean {
    if (this.projectParameterList.some((item) => item.name === '')) {
      this.warningMessage = GlobalConstants.INPUT_PARAMETER_NAME_ERROR;
      return this.getWarningInfo(AlertStatus.Warning);
    }

    if (this.getDuplicatesProjectParameterName(this.projectParameterList).length > 0) {
      this.warningMessage = GlobalConstants.DUPLICATE_PARAMETER_ERRORS;
      return this.getWarningInfo(AlertStatus.Warning);
    }

    if (this.projectParameterList.some((item) => item.value === '' && item.lstSizes.length === 0)) {
      this.warningMessage = GlobalConstants.INPUT_PARAMETER_VALUE_ERROR;
      return this.getWarningInfo(AlertStatus.Warning);
    }
    return true;
  }

  /**
   * Handles the change event when a parameter value is modified.
   * @param e - The event object.
   * @param index - The index of the parameter in the projectParameterList array.
   */
  public onParameterValueChange(e: Event, index: number): void {
    if (e.target) {
      this.projectParameterList[index].value = e.target['value'];
    }
  }

  /**
   * Saves the local project parameters data by retrieving the parameters from storage,
   * updating the project parameters with the corresponding values from the project parameter list,
   * and then reassigning the updated parameters back to the edit family item.
   *
   * @remarks
   * This method performs the following steps:
   * 1. Retrieves the local project parameters from storage.
   * 2. Updates each parameter in the project parameters list with the corresponding values from the project parameter list.
   * 3. Reassigns the updated parameters back to the edit family item.
   *
   * @returns void
   */
  public saveLocalProjectParametersData(): void {
    this.projectParameterList.forEach((element: Param) => {
      const parameter = this.projectParameterList.find((x) => x.index === element.index);
      if (parameter) {
        element.value = parameter.value;
        element.category = parameter.category;
        element.group = parameter.group;
        element.groupParameterUnder = parameter.groupParameterUnder;
        element.isInstance = parameter.isInstance;
        element.isShared = parameter.isShared;
        element.lstSizes = parameter.lstSizes;
        element.isPushOption = parameter.isPushOption;
        element.index = parameter.index;
        element.type = parameter.type;
        element.name = parameter.name;
      }
    });
  }

  /**
   * Handles the click event when the dismiss button is clicked.
   */
  public onDismissClick(): void {
    this.facade.changeDialogDimensions(605);
    this.warningMessage = '';
  }

  /**
   * Triggers the action to open the dialog for adding a new project parameter.
   * Utilizes the facade to send a routing action with the specified dialog constant.
   *
   * @remarks
   * This method is responsible for initiating the process of adding a new project parameter
   * by opening the corresponding dialog.
   */
  public addNewProjectParameter(): void {
    this.facade.sendPopupRoutingAction(GlobalConstants.ADD_NEW_PROJECT_PARAMETER_DIALOG);
  }

  /**
   * Returns the CSS class name for a given row based on the index and project parameter.
   * @param index - The index of the row.
   * @param projectParameter - The project parameter.
   * @returns The CSS class name for the row.
   */
  public getClassForRow(index: number, projectParameter: Param): string {
    const check = projectParameter.isPushedChecked;
    if (index % 2 !== 0 && check) {
      return GlobalConstants.ODD_BACKGROUND_SELECTED;
    } else if (index % 2 !== 0 && !check) {
      return GlobalConstants.ODD_BACKGROUND_UNSELECTED;
    } else if (index % 2 === 0 && check) {
      return GlobalConstants.EVEN_BACKGROUND_SELECTED;
    } else {
      return GlobalConstants.EVEN_BACKGROUND_UNSELECTED;
    }
  }

  //#end region

  //#region Private methods

  /**
   * Updates the project parameter value at the specified index with the provided event data.
   *
   * @param projectParameter - The project parameter to update.
   * @param index - The index of the project parameter in the list.
   * @param event - The event data containing the updated value.
   */
  private pushUpdatedProjectParameterValue(projectParameter: Param, index: number, event: any): void {
    if (index >= 0) {
      this.projectParameterList.splice(index, 1, {
        name: event.value.name,
        value: '',
        type: event.value.type,
        category: event.value.category,
        group: event.value.group,
        groupParameterUnder: event.value.groupParameterUnder,
        isInstance: event.value.isInstance,
        isShared: event.value.isShared,
        lstSizes: projectParameter.lstSizes,
        isPushOption: this.editFamilyItem.itemType === GlobalConstants.ELECTRICAL_TYPE_ITEMS,
        index: index,
        isPushedChecked: false,
      });
      if (projectParameter.name == '') {
        this.projectParameterList[index].value = '';
        this.projectParameterList[index].lstSizes = [];
      }
    }
  }

  /**
   * Returns an array of duplicate `Param` objects based on the `name` property.
   * @param array - The array of `Param` objects to check for duplicates.
   * @returns An array of duplicate `Param` objects.
   */
  private getDuplicatesProjectParameterName(array: Param[]): Param[] {
    return array.filter((value, index, self) => {
      return self.map((item) => item.name).indexOf(value.name) !== index;
    });
  }

  /**
   * Gets the warning information.
   * Sets the alert type to Warning and changes the dialog dimensions.
   */
  private getWarningInfo(alertStatus: AlertStatus): boolean {
    this.alertStatus = alertStatus;
    this.facade.changeDialogDimensions(680);
    return false;
  }

  /**
   * Updates the local parameters based on the editFamilyItem parameters.
   * If the editFamilyItem parameters is an array, it iterates through each element,
   * assigns the index and adds it to the localParams array.
   * Finally, it updates the local storage with the updated localParams.
   */
  private updateLocalParams(): void {
    let item = this.facade.getEditFamilyItem();

    if (Array.isArray(item.parameters)) {
      item.parameters.forEach((element: Param, i: number) => {
        element.index = i;
        this.projectParameters.push(element);
      });
    }
  }

  /**
   * Resets the data for the project data component.
   * Sets the visibility of the push checkbox based on the item type.
   * Clears the revitDocParameterList, projectParameterList, and localParams arrays.
   * Calls the fillParams method to populate the arrays with new data.
   */
  private resetData(): void {
    this.isPushCheckBoxVisible = this.editFamilyItem.itemType === GlobalConstants.ELECTRICAL_TYPE_ITEMS;
    this.revitDocParameterList = [];
    this.projectParameterList = [];
    this.projectParameters = [];
  }

  /**
   * Updates the push parameters if the push is false.
   * @param projectParameter - The project parameter.
   * @param itemIndex - The index of the item.
   */
  private updatePushParametersIfPushIsFalse(itemIndex: number): void {
    if (this.projectParameterList[itemIndex].value === '-') {
      this.projectParameterList[itemIndex].value = '';
    }
    this.projectParameterList[itemIndex].isPushOption = false;
    this.projectParameterList[itemIndex].isPushedChecked = false;
  }

  /**
   * Updates the push parameters if push is true.
   *
   * @param itemIndex - The index of the item.
   * @param projectParameter - The project parameter.
   */
  private updatePushParametersIfPushIsTrue(itemIndex: number): void {
    if (this.projectParameterList[itemIndex].value === '') {
      this.projectParameterList[itemIndex].value = '-';
    }
    this.projectParameterList[itemIndex].isPushOption = true;
    this.projectParameterList[itemIndex].isPushedChecked = true;
  }

  private updatePushParameterInEditFamilyItem(projectParameter: Param, needToDelete: boolean = true): void {
    if (!projectParameter.isPushOption || needToDelete) {
      // Split the pushParameters string into an array
      const pushParametersArray = this.pushParameters.split(',');

      // Find the index of the projectParameter name in the array
      const index = pushParametersArray.findIndex((param) => param === projectParameter.name);

      // If the parameter is found, remove it from the array
      if (index !== -1) {
        pushParametersArray.splice(index, 1);

        // Join the array back into a string and update pushParameters
        this.pushParameters = pushParametersArray.join(',');
      }
    } else {
      if (this.pushParameters === '') {
        this.pushParameters = projectParameter.name;
      } else {
        this.pushParameters += ',' + projectParameter.name;
      }
    }
    const paramToUpdate = this.projectParameterList.find((x) => x.index === projectParameter.index);
    if (paramToUpdate) {
      paramToUpdate.value = projectParameter.value;
      paramToUpdate.isPushedChecked = projectParameter.isPushedChecked;
    }
  }

  private updateData(): void {
    this.editFamilyItem.parameters = [] as any;
    this.projectParameterList.forEach((element: Param) => {
      (this.editFamilyItem.parameters as any).push(element);
    });
    this.editFamilyItem.pushParameters = this.pushParameters;
    this.facade.updateLocalStorage(GlobalConstants.FAMILY_INFORMATION_ITEM, this.editFamilyItem);
  }

  //#end region
}
